import Link from 'next/link'
import { useRouter } from 'next/router'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

const internal = {
  openInDocs: { en: 'Open in docs', tr: 'Dokümanlarda açın' },
  readGuide: { en: 'Read the guide', tr: 'Rehberi okuyun' },
  min: { en: 'min', tr: 'dk' },
}

export interface LearnEntry {
  label: string
  description: string
  href: string // internal /learn/... or external help.monofor.com link
  external?: boolean
  minutes?: number
}

export interface LearnGroup {
  category: string
  intro: string
  entries: LearnEntry[]
}

interface LearnHubGridProps {
  groups: LearnGroup[]
}

export default function LearnHubGrid({ groups }: LearnHubGridProps) {
  return (
    <Section tone="subtle" density="default">
      <Container size="xl">
        <div className="space-y-16">
          {groups.map((g) => (
            <div key={g.category}>
              <div className="max-w-2xl">
                <Eyebrow>{g.category}</Eyebrow>
                <Heading as="h2" size="display-md" className="mt-4">
                  {g.intro}
                </Heading>
              </div>
              <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {g.entries.map((entry, i) => (
                  <motion.div
                    key={entry.href}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, ease: EASE, delay: (i % 3) * 0.05 }}
                  >
                    <EntryCard entry={entry} />
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  )
}

function EntryCard({ entry }: { entry: LearnEntry }) {
  const { locale } = useRouter()
  const lang = locale === 'tr' ? 'tr' : 'en'
  const inner = (
    <>
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="text-body-md font-medium text-ink-primary">
            {entry.label}
          </div>
          {entry.minutes && (
            <span className="inline-flex items-center rounded-pill bg-surface-subtle px-2 py-0.5 text-caption tabular-nums text-ink-tertiary">
              {entry.minutes} {internal.min[lang]}
            </span>
          )}
        </div>
        <Text size="sm" tone="tertiary" className="mt-2">
          {entry.description}
        </Text>
      </div>
      <div className="mt-5 inline-flex items-center gap-1.5 text-body-sm font-medium text-brand-900 transition group-hover:translate-x-0.5">
        {entry.external ? internal.openInDocs[lang] : internal.readGuide[lang]}
        <ArrowGlyph className="h-3.5 w-3.5" />
      </div>
    </>
  )

  const className =
    'group flex h-full flex-col justify-between rounded-2xl border border-line-subtle bg-surface p-6 transition duration-300 ease-smooth hover:-translate-y-0.5 hover:border-line hover:shadow-card-hover'

  if (entry.external) {
    return (
      <a
        href={entry.href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {inner}
      </a>
    )
  }
  return (
    <Link href={entry.href} className={className}>
      {inner}
    </Link>
  )
}

function ArrowGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
